import { RefreshCw } from 'lucide-react'

/**
 * On a record's detail: the engine now reads this package differently.
 *
 * A re-parse REPLACES values somebody may have chosen on purpose, so it is only
 * ever a proposal, and each field is a question for a person. The questions are
 * answered one field at a time in the Data Intelligence Review; this says that
 * they are waiting and how many would overwrite an answer somebody gave, and
 * opens the review on the first.
 */
export default function ReparseNotice({
  proposals,
  onOpen,
  disabled = false,
}: {
  proposals: { field: string; humanDecided?: boolean }[]
  /** Opens the review on the first proposal. */
  onOpen: (field: string) => void
  disabled?: boolean
}) {
  if (proposals.length === 0) return null
  const decided = proposals.filter((p) => p.humanDecided).length
  const n = proposals.length
  return (
    <div className="panel border-sky-400/20" data-testid="reparse-notice">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <RefreshCw size={11} className="text-sky-400" />
          <span className="panel-header-title text-sky-300">Re-parse — the engine reads this differently</span>
        </div>
        <span className="font-mono text-[9px] uppercase text-stone-500">
          {n} field{n === 1 ? '' : 's'}
        </span>
      </div>
      <div className="panel-body space-y-2">
        <p className="text-[10px] leading-relaxed text-stone-500">
          Nothing has been changed. {n === 1 ? 'One field' : `${n} fields`} of this package now
          read differently from what the record holds. Each is answered on its own in the
          Data Intelligence Review.
          {decided > 0 && (
            <span className="text-amber-300/90">
              {' '}
              {decided === 1 ? 'One of them is a value' : `${decided} of them are values`} a person
              decided; taking the engine’s reading would overrule them, and the review asks first.
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={() => onOpen(proposals[0].field)}
          disabled={disabled}
          className="rounded border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-[11px] text-sky-200 disabled:opacity-40"
        >
          Review {n === 1 ? 'it' : `these ${n}`}
        </button>
      </div>
    </div>
  )
}
